import { useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import type { TenantUserActivationFragment } from "@/v2-backoffice-client/tenant/migration/__types__/tenant.migration.fragment"
import MigrationProgress from "@/v2-backoffice-client/tenant/migration/MigrationProgress"
import MigrationCenterMemberRow from "@/v2-backoffice-client/tenant/migration/center/MigrationCenterMemberRow"
import MigrationCenterMemberSortHeader from "@/v2-backoffice-client/tenant/migration/center/MigrationCenterMemberSortHeader"
import MigrationCenterScopeSendButton from "@/v2-backoffice-client/tenant/migration/center/MigrationCenterScopeSendButton"
import type { ActivationRunOutcome } from "@/v2-backoffice-client/tenant/migration/tenantMigration.constants"
import type {
  MigrationCenterMemberSort,
  MigrationCenterMemberSortKey,
  MigrationCenterTenantDescriptor,
} from "@/v2-backoffice-client/tenant/migration/center/tenant.migration.center.types"
import {
  rollupFromRows,
  sendableRows,
  sortRows,
} from "@/v2-backoffice-client/tenant/migration/center/tenant.migration.center.utils"

export type MigrationCenterTeamCardProps = {
  tenant: MigrationCenterTenantDescriptor
  rows: TenantUserActivationFragment[]
  isLoading: boolean
  /** Sticky offset: below the customer header when one renders. */
  stickyBelowCustomerHeader: boolean
  sort: MigrationCenterMemberSort
  onSort(key: MigrationCenterMemberSortKey): void
  outcomes: Record<number, ActivationRunOutcome>
  onOutcome(userId: number, outcome: ActivationRunOutcome): void
  onExclude(userId: number): void
  onChanged?(): void
}

/**
 * One team of the Migration Center: a sticky, collapsible header carrying
 * the team's progress and send button, over a sortable member table. The
 * send targets exactly the listed members.
 */
function MigrationCenterTeamCard(props: MigrationCenterTeamCardProps) {
  const {
    tenant,
    rows,
    isLoading,
    stickyBelowCustomerHeader,
    sort,
    onSort,
    outcomes,
    onOutcome,
    onExclude,
    onChanged,
  } = props

  const [collapsed, setCollapsed] = useState(false)

  return (
    <div className="rounded-lg border border-border">
      <div
        className={cn(
          "sticky z-10 flex h-9 items-center gap-3 rounded-t-lg border-b border-border bg-muted px-3",
          stickyBelowCustomerHeader ? "top-9" : "top-0",
          collapsed && "rounded-b-lg border-b-0",
        )}
      >
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
          data-feature-code="UI_MigrationCenter_TeamCollapse"
          onClick={() => {
            setCollapsed((prev) => !prev)
          }}
        >
          <Icon
            src={collapsed ? "angle-small-right" : "angle-small-down"}
            size={14}
            className="shrink-0 text-muted-foreground"
          />
          <span className="truncate text-sm font-medium">
            {tenant.tenantName}
          </span>
        </button>
        <MigrationProgress rollup={rollupFromRows(rows)} className="w-44" />
        <MigrationCenterScopeSendButton
          teams={[
            {
              tenantId: tenant.tenantId,
              userIds: sendableRows(rows).map((r) => r.UserId),
            },
          ]}
          scopeLabel={tenant.tenantName}
          onOutcome={onOutcome}
          onChanged={onChanged}
        />
      </div>
      {!collapsed && (
        <table className="w-full">
          <thead>
            <tr className="border-b border-border/60 text-left text-xs text-muted-foreground">
              <MigrationCenterMemberSortHeader
                label="Member"
                sortKey="name"
                sort={sort}
                onSort={onSort}
              />
              <MigrationCenterMemberSortHeader
                label="App"
                sortKey="app"
                sort={sort}
                onSort={onSort}
              />
              <MigrationCenterMemberSortHeader
                label="Sent"
                sortKey="sent"
                sort={sort}
                onSort={onSort}
              />
              <MigrationCenterMemberSortHeader
                label="Status"
                sortKey="status"
                sort={sort}
                onSort={onSort}
              />
              <th className="px-2 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {sortRows(rows, sort).map((row) => (
              <MigrationCenterMemberRow
                key={row.UserId}
                tenantId={tenant.tenantId}
                row={row}
                outcome={outcomes[row.UserId]}
                onOutcome={onOutcome}
                onExclude={onExclude}
                onChanged={onChanged}
              />
            ))}
            {rows.length === 0 && !isLoading && (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-2 text-xs text-muted-foreground"
                >
                  No members listed — the app filter may hide them
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  )
}

export default MigrationCenterTeamCard
